import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PortalShell from "@/components/portal/PortalShell";
import { Loader2, CheckCircle2, ShieldCheck, MessageSquareQuote } from "lucide-react";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const helpCta = (
  <a href="mailto:ops@connex.is" data-testid="answer-help"
     className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors">
    Need a hand?
  </a>
);

/** ClientAnswer — the client answers the lender's question. No login, phone-first, one field. */
export default function ClientAnswer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/apply/question/${token}`);
        if (alive) { setData(r.data); setSent(!!r.data.answered); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "That link isn't valid — ask whoever sent it for a new one");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (answer.trim().length < 1) return;
    setSending(true);
    try {
      await api.post(`/apply/question/${token}/answer`, { answer: answer.trim() });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "That didn't send — please try again");
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0B1220] grid place-items-center"><Loader2 className="size-6 animate-spin text-[#7da9ff]" /></div>;
  }

  if (err && !data) {
    return (
      <PortalShell variant="apply" kind="introducer" ctas={helpCta}
                   eyebrow="Connexis · Your application" title="We couldn't open that link">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 max-w-xl" data-testid="answer-error">
          <p className="text-[14px] text-slate-300 leading-relaxed">{err}</p>
          <p className="mt-3 text-[13px] text-slate-400">
            Links are private to one application and expire after 30 days. Reply to the message you
            received and we&apos;ll send a fresh one straight away.
          </p>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell variant="apply" kind="introducer" ctas={helpCta}
                 eyebrow="Connexis · Your application"
                 title={sent ? "Thank you — that's with us" : "One quick question"}
                 subtitle={data.company_name
                   ? `For ${data.company_name}${data.reference ? ` · ${data.reference}` : ""}`
                   : "Your finance application"}>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="answer-card">
          <div className="flex items-start gap-2.5">
            <MessageSquareQuote className="size-4 text-[#0055FF] mt-1 shrink-0" />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">
                The lender asked
              </div>
              <p className="mt-2 text-[15px] leading-relaxed font-semibold text-slate-900"
                 data-testid="answer-question">
                {data.question}
              </p>
            </div>
          </div>

          {sent ? (
            <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-teal-200 bg-teal-50 p-4"
                 data-testid="answer-received">
              <CheckCircle2 className="size-4 text-teal-600 mt-0.5 shrink-0" />
              <div className="text-[13px] text-teal-900 leading-relaxed">
                <b>Your answer is with us.</b> We&apos;ve passed it to the lender considering your
                application — there&apos;s nothing else you need to do.
                {(data.answer || answer) && (
                  <div className="mt-2.5 rounded-lg bg-white/70 p-3 text-[13px] text-slate-700"
                       data-testid="answer-echo">
                    “{data.answer || answer}”
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-5">
              <label className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                Your answer
              </label>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={6}
                maxLength={4000}
                placeholder="Type as much or as little as you like — plain English is perfect."
                data-testid="answer-input"
                className="mt-1.5 w-full rounded-[12px] border border-slate-200 bg-white p-3.5 text-[14px] leading-relaxed text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#0055FF]"
              />
              {err && <p className="mt-2 text-[12.5px] text-red-600" data-testid="answer-send-error">{err}</p>}
              <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11.5px] text-slate-400">{answer.length}/4000</span>
                <button onClick={submit} disabled={sending || answer.trim().length < 1}
                        data-testid="answer-submit"
                        className="inline-flex h-11 items-center rounded-full bg-[#0055FF] px-6 text-[13.5px] font-bold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-50">
                  {sending ? "Sending…" : "Send my answer"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <ShieldCheck className="size-4 text-[#7da9ff]" /> Private to your application
            </div>
            <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">
              This page belongs to your application only. Your answer goes to Connexis and on to the
              lender considering it — nobody else.
              {data.expires_at ? ` It works until ${fmt(data.expires_at)}.` : ""}
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-[13px] font-semibold text-white">Why we&apos;re asking</div>
            <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">
              Lenders ask for context rather than paperwork when they&apos;re close to a decision.
              Answering today usually saves a week.
            </p>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
